import { isPlatformServer } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, Optional, PLATFORM_ID } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { ScriptService } from '@core/services/script.service';
import { SeoService } from '@core/services/seo.service';
import { langFromLocale, trackById } from '@core/utils/utils';
import { environment } from '@environment';
import { Links, Metas } from '@features/companies/seo-vars';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { LocaleEnum } from '@models/commons/locales.enum';
import { Company } from '@models/company/company';
import { CustomTab } from '@models/company/custom-tab';
import { Publication } from '@models/company/publication';
import { Bookmark } from '@models/profile/bookmark';
import { Profile } from '@models/profile/profile';
import { JobFilter } from '@models/search/job-filter';
import { JobSearchContextEnum } from '@models/search/job-search-context.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import { CompanyRoutingFragmentEnum } from '@routing/company/company-routing.enum';
import { LinkRel, TypesEnum } from '@routing/directories/directories-routing.enum';
import { Follow, Unfollow } from '@store/company/company.actions';
import { CompanyState } from '@store/company/company.state';
import { GetCustomForms } from '@store/company/custom-forms/custom-forms.actions';
import { GetNews } from '@store/company/news/news.actions';
import { NewsState } from '@store/company/news/news.state';
import { ProfileState } from '@store/ngxs-profile/profile.state';
import { ApplicationStoreService } from '@store/profile/application-store.service';
import { BookmarkState } from '@store/profile/bookmark/bookmark.state';
import { JobSearchLoad } from '@store/search/job-search/job-search.actions';
import { JobSearchState } from '@store/search/job-search/job-search.state';
import { SessionState } from '@store/session/session.state';
import { BugsnagService } from '@wizbii/angular-bugsnag';
import { buildPath, WINDOW } from '@wizbii/angular-utilities';
import { Request } from 'express';
import { Observable } from 'rxjs';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewComponent implements OnInit, OnDestroy {
  @Select(BookmarkState) bookmarkMap$: Observable<Map<string, Bookmark>>;
  @Select(JobSearchState.result(JobSearchContextEnum.Company, 'nbHits')) totalCompanyJobs$: Observable<number>;
  @Select(JobSearchState.result(JobSearchContextEnum.Partner, 'nbHits')) totalPartnersJobs$: Observable<number>;
  @Select(SessionState.isLogged) isLogged$: Observable<boolean>;
  @Select(ProfileState.authenticatedProfile) profile$: Observable<Profile>;

  applicationsTotal$: Observable<number>;
  publications$: Observable<Publication[]>;
  company$: Observable<Company>;

  basePath: string;
  companyRoutingFragmentEnum = CompanyRoutingFragmentEnum;

  trackById = trackById;
  private metaElements: HTMLMetaElement[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly applicationStoreService: ApplicationStoreService,
    private readonly store: Store,
    private readonly seoService: SeoService,
    private readonly metaService: Meta,
    private readonly scriptService: ScriptService,
    @Inject(WINDOW) private readonly window: any,
    @Optional() @Inject(REQUEST) private readonly request: Request,
    @Inject(PLATFORM_ID) private readonly platformId: Record<string, unknown>,
    private readonly errorHandlerService: BugsnagService
  ) {
    this.applicationsTotal$ = this.applicationStoreService.total$;
  }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((paramMap) => paramMap.get('companyId')),
        tap((companyIdFromUrl) => {
          this.basePath = buildPath(FeaturesRoutingEnum.Company, companyIdFromUrl);
        }),
        switchMap((companyIdFromUrl) => this.store.select(CompanyState.company(companyIdFromUrl))),
        withLatestFrom(this.isLogged$),
        untilDestroyed(this)
      )
      .subscribe(([company, isLogged]) => {
        this.company$ = this.store.select(CompanyState.company(company.id));
        this.publications$ = this.store.select(NewsState.publications(company.id));

        this.init(company.id);

        if (!isLogged) {
          this.initSeoData(company);
        }

        if (!this.window.twttr) {
          this.scriptService.load('https://platform.twitter.com/widgets.js').subscribe({
            error: (err) => {
              this.errorHandlerService.sendError(
                { errorClass: err, errorMessage: 'Unable to load Twitter Widgets' },
                'warning'
              );
            },
          });
        }
      });
  }

  @Dispatch() init = (companyId: string): any[] => [
    new JobSearchLoad(JobSearchContextEnum.Company, new JobFilter({ company: companyId })),
    new JobSearchLoad(JobSearchContextEnum.Partner, new JobFilter({ partner: companyId })),
    new GetNews(companyId),
    new GetCustomForms(companyId),
  ];

  ngOnDestroy(): void {
    if (!this.store.selectSnapshot(SessionState.isLogged)) {
      this.seoService.removeLinkWithRel(LinkRel.Canonical);

      Links.forEach((link) => {
        this.seoService.removeLinkTag(link);
      });
      this.metaElements.forEach((meta) => {
        this.metaService.removeTagElement(meta);
      });
    }
  }

  follow(companyId: string): void {
    if (!this.store.selectSnapshot(SessionState.isLogged)) {
      this.redirectForRegister();
    } else {
      this.store.dispatch(new Follow(companyId));
    }
  }

  unfollow(companyId: string): void {
    if (!this.store.selectSnapshot(SessionState.isLogged)) {
      this.redirectForRegister();
    } else {
      this.store.dispatch(new Unfollow(companyId));
    }
  }

  cannotDisplayCompany(company: Company, isLogged: boolean): boolean {
    return company.status === 'BANNI' || (!isLogged && company.status === 'PRIVE');
  }

  displayCustomTab(profile: Profile, customTab: CustomTab): boolean {
    const isLogged = this.store.selectSnapshot(SessionState.isLogged);
    const customTabHasContent = customTab && customTab.description && customTab.description.trim() !== '';
    const customTabHasForm =
      customTab && customTab.description && customTab.description.toLowerCase().indexOf('%form%') > -1;
    const profileIsInFrance =
      profile && profile.location && profile.location.country && profile.location.country.toLowerCase() === 'france';
    const isLocaleFrance = environment.locale === LocaleEnum.fr_FR;

    return isLogged
      ? customTabHasForm
        ? customTabHasContent && profileIsInFrance
        : customTabHasContent
      : isLocaleFrance;
  }

  private redirectForRegister() {
    const redirect =
      this.request && isPlatformServer(this.platformId)
        ? `${this.request.protocol}://${this.request.get('host')}${this.request.url}`
        : `${this.window.location.origin}${this.window.location.pathname}`;

    this.window.open(`${environment.urls.accountSignup}${redirect}`, '_self');
  }

  private initSeoData(company: Company): void {
    const lang = langFromLocale(company.locale);
    this.seoService.createLinkForCanonicalURL(lang, TypesEnum.company, { companyId: company.id });

    this.seoService.addLinkTags(Links);
    this.metaElements = [
      this.metaService.addTag({
        name: 'author',
        content: company.name,
      }),
      this.metaService.addTag({
        itemprop: 'name',
        content: company.tagLine,
      }),
      this.metaService.addTag({
        name: 'twitter:image:src',
        content: `https://images.wizbii.com/company/logo?slug=${company.id}&size=large@2x`,
      }),
      this.metaService.addTag({
        property: 'og:url',
        content: `https://www.wizbii.com/company/${company.id}`,
      }),
      this.metaService.addTag({
        property: 'og:image',
        content: `https://images.wizbii.com/company/logo?slug=${company.id}&size=large@2x`,
      }),
      this.metaService.addTag({
        property: 'article:author',
        content: company.name,
      }),
      this.metaService.addTag({
        property: 'article:publisher',
        content: company.name,
      }),
      this.metaService.addTag({
        itemprop: 'image',
        content: `https://images.wizbii.com/company/logo?slug=${company.id}&size=large@2x`,
      }),
      ...this.metaService.addTags(Metas),
    ];
  }
}
